import {
  API_ROUTES,
  type DocumentCreateRequest,
  type DocumentCreateResponse,
  type DocumentEntity,
  type DocumentImageResponse,
  type DocumentListResponse,
  type DocumentRenderResponse,
  type DocumentSection,
  type DocumentSectionCreateRequest,
  type DocumentSectionListResponse,
  type DocumentSectionPositionRequest,
  type DocumentSectionWriteRequest,
  type DocumentUpdateRequest,
  type PlanImageMediaType,
} from '@oscom/shared';
import { apiGet } from './client';
import { jsonInit, sendAndUnwrap } from './send';

function documentUrl(customerId: string, documentId: string): string {
  return `${API_ROUTES.customerDocuments(customerId)}/${encodeURIComponent(documentId)}`;
}

function sectionUrl(customerId: string, documentId: string, sectionId: string): string {
  return `${documentUrl(customerId, documentId)}/sections/${encodeURIComponent(sectionId)}`;
}

export function listDocuments(customerId: string): Promise<DocumentListResponse> {
  return apiGet<DocumentListResponse>(API_ROUTES.customerDocuments(customerId));
}

export async function createDocument(
  customerId: string,
  request: DocumentCreateRequest,
): Promise<DocumentEntity> {
  const response = await sendAndUnwrap(
    API_ROUTES.customerDocuments(customerId),
    jsonInit('POST', request),
    'Anlegen',
  );
  return ((await response.json()) as DocumentCreateResponse).document;
}

export function getDocument(customerId: string, documentId: string): Promise<DocumentEntity> {
  return apiGet<DocumentEntity>(documentUrl(customerId, documentId));
}

export async function saveDocument(
  customerId: string,
  documentId: string,
  write: DocumentUpdateRequest,
): Promise<DocumentEntity> {
  const response = await sendAndUnwrap(
    documentUrl(customerId, documentId),
    jsonInit('PUT', write),
    'Speichern',
  );
  return (await response.json()) as DocumentEntity;
}

export async function deleteDocument(customerId: string, documentId: string): Promise<void> {
  await sendAndUnwrap(documentUrl(customerId, documentId), { method: 'DELETE' }, 'Löschen');
}

export function listSections(
  customerId: string,
  documentId: string,
): Promise<DocumentSectionListResponse> {
  return apiGet<DocumentSectionListResponse>(`${documentUrl(customerId, documentId)}/sections`);
}

export async function createSection(
  customerId: string,
  documentId: string,
  request: DocumentSectionCreateRequest,
): Promise<DocumentSection> {
  const response = await sendAndUnwrap(
    `${documentUrl(customerId, documentId)}/sections`,
    jsonInit('POST', request),
    'Anlegen',
  );
  return (await response.json()) as DocumentSection;
}

export async function saveSection(
  customerId: string,
  documentId: string,
  sectionId: string,
  write: DocumentSectionWriteRequest,
): Promise<DocumentSection> {
  const response = await sendAndUnwrap(
    sectionUrl(customerId, documentId, sectionId),
    jsonInit('PUT', write),
    'Speichern',
  );
  return (await response.json()) as DocumentSection;
}

export async function moveSection(
  customerId: string,
  documentId: string,
  sectionId: string,
  position: DocumentSectionPositionRequest,
): Promise<DocumentSection> {
  const response = await sendAndUnwrap(
    `${sectionUrl(customerId, documentId, sectionId)}/position`,
    jsonInit('PUT', position),
    'Verschieben',
  );
  return (await response.json()) as DocumentSection;
}

export async function deleteSection(
  customerId: string,
  documentId: string,
  sectionId: string,
): Promise<void> {
  await sendAndUnwrap(
    sectionUrl(customerId, documentId, sectionId),
    { method: 'DELETE' },
    'Löschen',
  );
}

/** The raw bytes with the type detected from them (readImageInfo), no multipart. */
export async function uploadDocumentImage(
  customerId: string,
  documentId: string,
  file: Blob,
  mediaType: PlanImageMediaType,
): Promise<DocumentImageResponse> {
  const response = await sendAndUnwrap(
    `${documentUrl(customerId, documentId)}/images`,
    { method: 'POST', headers: { 'content-type': mediaType }, body: file },
    'Hochladen',
  );
  return (await response.json()) as DocumentImageResponse;
}

/** Images never change under their URL (a new upload is a new asset id). */
export function documentImageUrl(customerId: string, documentId: string, assetId: string): string {
  return `${documentUrl(customerId, documentId)}/images/${encodeURIComponent(assetId)}`;
}

export function renderDocument(
  customerId: string,
  documentId: string,
): Promise<DocumentRenderResponse> {
  return apiGet<DocumentRenderResponse>(`${documentUrl(customerId, documentId)}/render`);
}
